export type { ArticleManagerEvent } from './manager';
export { ArticleManager } from './manager';
export type {
  AuthorId,
  NodeId,
  CampaignStrategy,
  ExpiredUrlType,
  Campaigns,
  Author,
  InternalTag,
  InternalAssetAttributes,
  InternalAsset,
  InternalNode,
  InternalNodeQuotes,
  Attributes,
  ImageAttributes,
  Resolution,
  InternalNodeCamelCase,
  GetArticleI,
  Content,
  Image,
  Stock,
  News,
  ResourceType,
  ExtractedArticleBlock,
  ArticleBlock,
  ArticleBlockPlaceholder,
  ArticleBlocksList,
  ArticleDFPTags,
  ArticleData,
  AuthorElement,
  RelevantArticles,
  RelevantArticlesIds,
  DraftArticle,
  TrendingIndiaPosts,
  Metrics,
  EditorialArticlePreviewResponse,
  EditorialArticlePreview,
  Disqus,
  FieldAccessRestricted,
  FieldImage,
  FieldImageData,
  FieldSlideshowBottomLink,
  SectorV2,
  Morningstar,
  Naic,
  Sic,
  WpPostMeta,
  Price,
  Taxonomy,
  WNSTNFollowUpQuestionsResponse,
  ExpiredUrlData,
  ExpiredUrlResponse,
  TopStoriesResponse,
  SponsoredContentHomepage,
  SponsoredContentHomepagePost,
  TrendingPostsHomepage,
  TrendingPostsHomepagePost,
  TrendingPostContent,
  TrendingPostContentAsset,
  TrendingPostContentTicker,
  SourceId,
  RecommendedArticle,
  RecommendedResponse,
} from './entities/article';

export type {
  CommentCountResponse,
  CommentsResponse,
  Data,
  DataSettings,
  Auth,
  Integrations,
  Facebook,
  FacebookTargetFilter,
  Sso,
  SsoTargetFilter,
  CharCount,
  CloseCommenting,
  CommunityGuidelines,
  DisableCommenting,
  Media,
  TopCommenter,
  Giphy,
  Organization,
  Reaction,
  RTE,
  Story,
  CommentCounts,
  Tags,
  Metadata,
  StorySettings,
  MessageBox,
  Site,
} from './entities/comments';

export {
  sanitizeTickerSymbol,
  tickerPlaceholderElement,
  injectTickerPlaceholders,
  tickerizeContent,
  getTickersPlaceHolders,
  tickerizeHTMlContent,
  extractSymbolFromHTML,
} from './tokenization';

export type { ArticleAdsManagerOptions } from './ArticleAdsManager';
export { ArticleAdsManager } from './ArticleAdsManager';
export {
  findBreakIndices,
  selectBreakIndices,
  bodyAdCountForBreaks,
  findFirstInterruptIndex,
  findLeadgenInsertIndex,
  prepareBlocksForInterrupts,
  relocateRecircAfterFirstInterrupt,
  splitOversizedLists,
  mergeUnbrokenListSplits,
  countPlacementUnits,
  FIRST_INTERRUPT_PARAGRAPHS,
  getBlockPlainText,
  getTopLevelListItems,
  isCountableBodyParagraph,
  MIN_FIRST_INTERRUPT_CHARS,
  MIN_FIRST_INTERRUPT_PARAGRAPHS,
  SHORT_NEWS_PARAGRAPH_COUNT,
  SHORT_NEWS_WORD_COUNT,
  suggestedBodyAdCount,
} from './adPlacement';

export { isSponsoredArticle, getSponsoredContentLabel, checkIfIsSponsoredSourceContent } from './sponsored-utils';

export { camelcaseKeysArticleData, getArticle, getArticleData, getInitialTickers } from './utils/article';

export { getArticleTerms } from './utils/article-terms';

export { isExpiredUrlActive, normalizeExpiredUrlApiResponse } from './expiredUrlSlug';

export {
  splitHTMLContent,
  splitHTMLContentRaw,
  parseArticleBodyToBlocks,
  formatElementBlock,
  convertAttributesToCamelCase,
  deduplicateElements,
} from './utils/parsing';

export {
  getPrimaryTickers,
  getArticleTagsNames,
  getNodeTagsNames,
  getArticleChannelsNames,
  getNodeChannelsNames,
  getArticleTickers,
  filterRelatedNodes,
  isTaxonomyExists,
} from './utils/taxonomy';

export {
  getWordsAndCharactersCount,
  checkIfArticleBodyIsShortherThan,
  calculateReadTime,
  splitElements,
  replaceAmpTwitterToP,
  replaceRedditHTML,
  printProHeadlineArticleContent,
  parseTagAttributes,
  generatePartialViewPreviewFromArticleBlocks,
  generatePreviewFromBody,
  type Tweet,
} from './utils/content';

export {
  insertCampaignInBlocks,
  insertCampaign,
  findPlaceholderPositionInParsedContent,
  findPlaceholderPlaceInBlocks,
  getArticleCampaignStrategy,
  injectNodeIdInCampaignBlocks,
  performRegexesOnCampaignHTML,
} from './utils/campaign';

export {
  attachThirdPartyDFPTargeting,
  setDFPTargeting,
  getDFPTargeting,
  getArticleVideoPlayerSOV,
  setArticleVideoPlayerSOV,
  getPrimisIdByTaxonomies,
  getSpecificArticlesPrimisID,
  getArticleKeywordsGroup,
  matchKeywords,
  injectVendorInArticleUrls,
  injectUtmSourceInUrls,
  getArticleCampaignQueryParams,
  getArticleReferer,
  setCampaignSessionReferrer,
  getCampaignSessionReferrer,
} from './utils/ads';

export { getSovPosition } from './utils/ads-taboola';

export {
  isProOnlyPost,
  isFullHeadline,
  isOfferingArticle,
  checkIfShouldLoadCalendar,
  getInitialTab,
  isTagged,
  isChanneled,
  isStoryTaggedPressRelease,
  automatedContentAuthorIds,
  isContentAutomated,
} from './utils/checks';

export { getTemplateFeatures, countElementsInBodyBlocks } from './utils/template';

export { getInfiniteScrollArticles, getInfiniteScrollArticlesIDs } from './utils/infinite-scroll';

export { getAuthorName, authorTypeGenerator } from './utils/author';

export { getCanonicalUrl } from './utils/url';

export {
  findPrimaryImage,
  getSecFormType,
  injectOptinMonsterInArticlePlaceholder,
  formatArticleSurrogateKeys,
  shouldDisableCampaignifyUnit,
} from './utils/misc';

export {
  articlesMetaInfo,
  comtexPublishers,
  comtexPublishersNoIndex,
  canArticleBeMarkedAsNoIndex,
  getQuoteDetailsFromSymbols,
} from './utils/meta';

export {
  buildVideoObjectSchema,
  extractVideoArticleBlock,
  formatVideoThumbnailUrl,
  getVideoArticleImage,
  getVideoArticleSummary,
  getVideoArticleTitle,
  getVideoArticleYoutubeId,
  getVideoPostMeta,
  isVideoArticle,
  isVideoBlock,
  LEGACY_VIDEO_ARTICLE_STORY_TYPE,
  VIDEO_ARTICLE_STORY_TYPE,
} from './utils/videoArticle';

export { getRelatedArticles } from './utils/related';

export { getPaywallType, getPaywallContentType, isPaywallArticle, type PaywallInput } from './utils/paywall';

export { getUserAccessTypeForSSR } from './utils/getUserAccessTypeForSSR';
